import Paper from "@mui/material/Paper";
import Typography from '@mui/material/Typography';
import { useAppSelector } from "@store/hooks";
import { PaymentType } from "@utils/BasicTypes";
import { CacheTypes } from "@utils/cache/interfaces";
import React from "react";
import { cacheUtils } from "../..";
import useDebugInformation from '../../hooks/useDebugInformation';
import { ui } from "../../interfaces/interfaces";
import { validCarePlansDiscounts } from "../Layout/BookingForm/MainForm/Steps/StepThree/RatesTab/DiscountsTab/discountsLogic";

const nameSpace = "CarePlanSideBar"

type CarePlanSideBarProps = {
    parent: string
}

const processSummary = (summary: string) => {
    const match1 = summary?.match(/\d\d\/\d\d\/\d\d\d\d/g) || null
    const match2 = summary?.match(/£\d+\.\d+/g) || null
    // either per year | per month| per quarter | per x months and followed by some optional text which we don't need
    const match3 = summary?.match(/per\s(\d+\smonths|year|month|quarter)/g) || null
    const first = (match1 && match1[0]) || ""
    const charge = (match2 && match2[0]) || ""
    const duration = (match3 && match3[0]) || ""
    return { first, charge, duration }
}

const CarePlanSideBar: React.FC<CarePlanSideBarProps> = ({ parent }) => {
    const debugName = `[${parent}]▷[${nameSpace}]`
    const carePlanSummary = useAppSelector(({ ui }) => ui.CarePlanSummary)
    const chargeRate = useAppSelector(({ ui }) => ui.RatesDiscount)
    const duration = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsDuration)
    const freq = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsBillingFrequency)
    const excess = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsExcess)
    const method = useAppSelector(({ ui }) => ui.CarePlanBillingMethodPaymentType)
    const serviceAgreementReference = useAppSelector(({ ui }) => ui.CarePlanReference)
    const serviceAgreementOffered = useAppSelector(({ ui }) => ui.CarePlanOffered)
    const serviceAgreementDeclinedReason = useAppSelector(({ ui }) => ui.CarePlanDeclinedReason)
    const serviceAgreementType = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsPlanType)
    const rateReductionDiscountId = useAppSelector(({ state }) => state.ChargeRateReductionDiscountId)
    const jobTypeCode = useAppSelector(({ state }) => state.JobTypeCode);
    const options = cacheUtils.getCache<CacheTypes.PaymentTypes>("PaymentTypes");
    const [state, setState] = React.useState({ first: "", charge: "", duration: "" })
    const [ddOnly, setDdOnly] = React.useState(false)

    React.useEffect(() => setState(processSummary(carePlanSummary)), [carePlanSummary])

    React.useEffect(() => {
        setDdOnly(
            (freq?.serviceAgreementBillingFrequencyId === 12 || freq?.serviceAgreementBillingFrequencyId === 18)
            && duration?.serviceAgreementDurationId === 18
        );
    }, [freq, duration]);

    React.useEffect(() => {
        ui.CarePlanBillingMethodPaymentType = ddOnly
            ? options.find(o => o?.name === "Direct Debit") as PaymentType ?? null
            : null;
    }, [ddOnly, options]);

    const validCCPlansDiscounts = React.useMemo(() => {
        return validCarePlansDiscounts(jobTypeCode);
    }, [jobTypeCode]);

    useDebugInformation(debugName, { parent, chargeRate, carePlanSummary })
    if (serviceAgreementOffered && !serviceAgreementDeclinedReason?.serviceAgreementDeclinedId && state.first)
        return (
            <Paper elevation={4} sx={{ m: 1, ml: 0 }}>
                <Typography variant="h6" sx={{ ml: 1, mr: 1, fontWeight: "bolder" }}>CARE PLAN: {serviceAgreementReference}</Typography>
                {serviceAgreementReference && <Typography variant="h6" sx={{ ml: 1, mr: 1, fontWeight: "bolder", color: !validCCPlansDiscounts.includes(rateReductionDiscountId) ? "red" : "green" }}>{!validCCPlansDiscounts.includes(rateReductionDiscountId) ? "OFFER REDUCED RATE" : `${serviceAgreementType?.name}`}</Typography>}
                <Typography variant="h6" sx={{ ml: 1, mr: 1 }}> <b>First Pay Date:</b> {state.first || ""}</Typography>
                <Typography variant="h6" sx={{ ml: 1, mr: 1 }}> <b>Duration:</b> {duration?.name || ""}</Typography>
                <Typography variant="h6" sx={{ ml: 1, mr: 1 }}> <b>Freq:</b> {freq?.name || ""}</Typography>
                <Typography variant="h6" sx={{ ml: 1, mr: 1 }}> <b>Charge:</b> {state.charge || ""} {state.duration || ""}</Typography>
                {(excess?.serviceAgreementExcessId! > 0) && (<Typography variant="h6" sx={{ ml: 1, mr: 1 }}> <b>Excess:</b> {excess?.excess}</Typography>)}
                {(ddOnly) && (<Typography variant="h6" sx={{ ml: 1, mr: 1, color: "red" }}> <b>Direct Debit Only</b></Typography>)}
                {method && <Typography variant="h6" sx={{ ml: 1, mr: 1 }}> <b>Pay method:</b> {method?.name || ""}</Typography>}
            </Paper >
        )
    return null
}

export default React.memo(CarePlanSideBar)